import { PopulatedOffering } from '~entities/offering/model/offering.types';
import { BookingRoom } from '../model/booking.types';
import { v4 as uuidv4 } from 'uuid';
import { queryClient } from '~shared/query-client';
import { configurationQueryOptions } from '~entities/configuration/api/configuration.queries';
import i18n, { Locale } from '~app/i18n';
import { Offer } from '~entities/offer/model/offer.types';
import { hideRoomOffersDuplicatedByPackages } from '~entities/offer/lib/offer.utils';
import { RoomFeature } from '~entities/room/model/room.types';

type PartialState = {
  rooms: Partial<BookingRoom>[];
  currentRoomId?: BookingRoom['id'];
};

export const createNewBookingRoom = (
  state: PartialState,
  payload?: Partial<BookingRoom>,
) => {
  const newRoom = {
    id: uuidv4(),
    index: state.rooms.length,
    ...payload,
  };
  state.rooms = [newRoom, ...state.rooms.filter(room => !!room.cartItemId)];
  state.currentRoomId = newRoom.id;
  return newRoom;
};

export const getCurrentBookingRoom = (state?: PartialState) => {
  if (!state) return undefined;
  return state.rooms.find(room => room.id === state.currentRoomId);
};

export const getOrCreateCurrentBookingRoom = (
  state: PartialState,
): Partial<BookingRoom> => {
  return getCurrentBookingRoom(state) ?? createNewBookingRoom(state);
};

export const sortOfferings = (
  offerings: PopulatedOffering[],
  sorting: 'relevance' | 'low-to-high' | 'high-to-low',
): PopulatedOffering[] =>
  sorting === 'relevance'
    ? offerings
    : [...offerings].sort((a, b) => {
        const aMinPrice = Math.min(
          ...a.offers.map(
            offer => offer.price?.amount ?? offer.minimumPrice?.amount ?? 0,
          ),
        );
        const bMinPrice = Math.min(
          ...b.offers.map(
            offer => offer.price?.amount ?? offer.minimumPrice?.amount ?? 0,
          ),
        );

        if (!aMinPrice || !bMinPrice) return 0;

        return sorting === 'low-to-high'
          ? aMinPrice - bMinPrice
          : bMinPrice - aMinPrice;
      });

export const populateOfferings = (offers: Offer[]) => {
  const configuration = queryClient.getQueryData(
    configurationQueryOptions(i18n.language as Locale).queryKey,
  );

  if (!configuration) return;

  const { rooms, packageCategories, lastMinutePackages } = configuration;

  return [
    ...rooms
      .map(room => ({
        ...room,
        // Drop the plain room offer when a package covers the same room at the
        // same price — the (better-value) package is shown instead. Different
        // packages at the same price all stay; only the regular room is hidden.
        offers: hideRoomOffersDuplicatedByPackages(
          offers.filter(
            ({ roomId, type }) =>
              roomId === room.id && (type === 'ROOM' || type === 'PACKAGE'),
          ),
        ),
      }))
      .filter(({ offers }) => offers.length > 0),
    ...packageCategories
      .flatMap(packageCategory => packageCategory.packages)
      .map(packageItem => ({
        ...packageItem,
        offers: offers.filter(
          ({ packageId, type }) =>
            packageId === packageItem.id && type === 'PACKAGE',
        ) as (Offer & { type: 'PACKAGE' })[],
      }))
      .filter(({ offers }) => offers.length > 0),
    ...lastMinutePackages
      .map(lastMinutePackage => ({
        ...lastMinutePackage,
        offers: offers.filter(
          ({ lastMinutePackageId, type }) =>
            lastMinutePackageId === lastMinutePackage.id &&
            type === 'LAST_MINUTE_PACKAGE',
        ),
      }))
      .filter(({ offers }) => offers.length > 0),
  ];
};

export const filterOfferings = (
  offerings: PopulatedOffering[],
  featuresFilter: RoomFeature[] | undefined,
) => {
  const configuration = queryClient.getQueryData(
    configurationQueryOptions(i18n.language as Locale).queryKey,
  );

  if (!configuration) return [];

  const { rooms } = configuration;

  return offerings.filter(offering => {
    const offeringRoom = rooms.find(
      room => room.id === offering.offers[0].roomId,
    );
    if (!offeringRoom) return false;

    if (featuresFilter) {
      return featuresFilter.every(feature =>
        offeringRoom.features.some(f => f.id === feature.id),
      );
    }

    return true;
  });
};
